import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import debug from 'debug';

const log = debug('rois:components:selectors:budget-item');

if (import.meta.env.DEV) {
  debug.enable('rois:components:selectors:budget-item*');
}
import SearchableDropdown from '../../inputs/SearchableDropdown';
import InputText from '../../inputs/InputText';
import { normalizeDecimalInput } from '../../../utils/decimalFormat';
import { MainButton } from '../../inputs/buttons/MainButton';
import { Typography } from '../../display/Typography';
import { GET_BUDGET_CATEGORIES } from '../../../gql/queries/budget/budgetCategories';
import { GET_BUDGET_ITEMS } from '../../../gql/queries/budget/budgetItems';
import {
  SelectorContainer,
  SelectorRow,
  PartidaWithPill,
  AmountGroup,
  AddButton,
  CategoryPill,
  ErrorText,
} from './styles';
import {
  BudgetItemSelectorProps,
  CategoryOption,
  BudgetItemOption,
} from './types';

export const BudgetItemSelector: React.FC<BudgetItemSelectorProps> = ({
  onAdd,
  existingItems = [],
  disabled = false,
}) => {
  const { t } = useTranslation(['workOrders', 'common']);

  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [selectedBudgetItemId, setSelectedBudgetItemId] = useState<string>('');
  const [quantity, setQuantity] = useState<string>('1');
  const [error, setError] = useState<string>('');

  // Query para obtener categorías
  const {
    data: categoriesData,
    loading: categoriesLoading,
    error: categoriesError,
  } = useQuery(GET_BUDGET_CATEGORIES, {
    fetchPolicy: 'cache-first',
  });

  // Query para obtener TODAS las partidas activas (sin filtro de categoría)
  const {
    data: budgetItemsData,
    loading: budgetItemsLoading,
    error: budgetItemsError,
  } = useQuery(GET_BUDGET_ITEMS, {
    variables: {
      filters: {
        isActive: true, // Solo partidas activas
      },
      pagination: null, // Sin paginación, traer todas
    },
    fetchPolicy: 'cache-and-network', // ✅ Siempre verificar con servidor
  });

  // Preparar opciones de categorías
  const categoryOptions: CategoryOption[] = React.useMemo(() => {
    if (!categoriesData?.budgetCategories) return [];

    // ✅ CORREGIDO: BudgetCategory NO tiene campo isActive
    // Todas las categorías están activas por defecto
    const categoryOptions = categoriesData.budgetCategories.map(
      (category: any) => ({
        value: category.id,
        label: `${category.code} - ${category.name}`,
        color: category.color,
      })
    );

    return categoryOptions;
  }, [categoriesData]);

  // Preparar opciones de partidas (TODAS las partidas activas, sin filtro por categoría)
  const budgetItemOptions: BudgetItemOption[] = React.useMemo(() => {
    if (!budgetItemsData?.budgetItems?.items) return [];

    return budgetItemsData.budgetItems.items
      .filter((item: any) => {
        // Filtrar partidas ya existentes
        const isAlreadyAdded = existingItems.some(
          (existing) => existing.budgetItemId === item.id
        );
        return !isAlreadyAdded;
      })
      .map((item: any) => ({
        value: item.id,
        label: `${item.code} - ${item.description}`,
        unitPrice: item.unitPrice,
        unit: item.unit,
        categoryId: item.budgetCategoryId,
        category: item.budgetCategory,
      }));
  }, [budgetItemsData, existingItems]);

  // Auto-seleccionar categoría cuando se selecciona una partida
  useEffect(() => {
    if (selectedBudgetItemId) {
      const selectedItem = budgetItemOptions.find(
        (item) => item.value === selectedBudgetItemId
      );
      log('🔍 Partida seleccionada:', {
        selectedBudgetItemId,
        selectedItem: selectedItem ? {
          code: selectedItem.label.split(' - ')[0],
          categoryId: selectedItem.categoryId,
          category: selectedItem.category?.name,
        } : null,
      });
      if (selectedItem && selectedItem.categoryId) {
        setSelectedCategoryId(selectedItem.categoryId);
        log('✅ Categoría auto-seleccionada:', {
          categoryId: selectedItem.categoryId,
          categoryName: selectedItem.category?.name,
        });
      }
    }
    setError('');
  }, [selectedBudgetItemId, budgetItemOptions]);

  // Validar y añadir partida
  const handleAdd = () => {
    log('🔴 handleAdd called');
    log(
      '🔴 selectedCategoryId:',
      selectedCategoryId
    );
    log(
      '🔴 selectedBudgetItemId:',
      selectedBudgetItemId
    );
    log('🔴 quantity:', quantity);

    setError('');

    // Validaciones (solo partida y cantidad, categoría se auto-selecciona)
    if (!selectedBudgetItemId) {
      log('❌ No budget item selected');
      setError(t('workOrders:selectBudgetItem'));
      return;
    }

    if (!selectedCategoryId) {
      log('❌ No category auto-selected');
      setError(t('workOrders:selectCategory'));
      return;
    }

    const quantityNum = parseFloat(quantity);
    if (isNaN(quantityNum) || quantityNum <= 0) {
      setError(t('validation:budgetItems.quantityRequired'));
      return;
    }

    // Verificar que no esté ya añadida
    const isAlreadyAdded = existingItems.some(
      (item) => item.budgetItemId === selectedBudgetItemId
    );
    if (isAlreadyAdded) {
      setError(t('workOrders:budgetItemAlreadyAdded'));
      return;
    }

    // Obtener información de la partida seleccionada
    const selectedItem = budgetItemOptions.find(
      (item) => item.value === selectedBudgetItemId
    );
    if (!selectedItem) {
      setError(t('common:errors.itemNotFound'));
      return;
    }

    // Llamar callback de añadir
    const itemToAdd = {
      budgetItemId: selectedBudgetItemId,
      quantity: quantityNum,
      unitPrice: selectedItem.unitPrice,
      unit: selectedItem.unit,
      code: selectedItem.label.split(' - ')[0],
      description: selectedItem.label.split(' - ')[1],
      category: {
        code:
          categoryOptions
            .find((cat) => cat.value === selectedCategoryId)
            ?.label.split(' - ')[0] || '',
        name:
          categoryOptions
            .find((cat) => cat.value === selectedCategoryId)
            ?.label.split(' - ')[1] || '',
        color:
          categoryOptions.find((cat) => cat.value === selectedCategoryId)
            ?.color || '#cccccc',
      },
    };

    log('✅ Calling onAdd with:', itemToAdd);
    onAdd(itemToAdd);

    // Limpiar formulario
    setSelectedBudgetItemId('');
    setQuantity('1');
  };

  return (
    <SelectorContainer>
      <SelectorRow>
        {/* Grupo izquierdo: Dropdown + Pill pegados */}
        <PartidaWithPill>
          <SearchableDropdown
            typeDropDown="Default"
            labelText={t('workOrders:selectBudgetItem')}
            placeholder={t('common:placeholders.select')}
            options={budgetItemOptions}
            selectedOption={selectedBudgetItemId}
            onChangeSelected={(option) => {
              setSelectedBudgetItemId(option?.value || '');
            }}
            inputSize="M"
            isDisabled={disabled || budgetItemsLoading}
            minSearchLength={2}
            noResultsText={t('workOrders:noBudgetItemsFound')}
          />
          {selectedCategoryId && (() => {
            const cat = categoryOptions.find((c) => c.value === selectedCategoryId);
            return cat ? (
              <CategoryPill color={cat.color || '#666666'}>
                {cat.label}
              </CategoryPill>
            ) : null;
          })()}
        </PartidaWithPill>

        {/* Grupo derecho: Cantidad + Botón pegados */}
        <AmountGroup>
          <InputText
            label={t('workOrders:quantity')}
            placeholder="1"
            value={quantity}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setQuantity(normalizeDecimalInput(e.target.value))
            }
            onKeyDown={(e) => {
              // Acepta coma/punto (separador decimal es-ES); bloquea letras/operadores.
              if (e.key.length === 1 && !/[\d.,]/.test(e.key)) {
                e.preventDefault();
              }
            }}
            typeInput="normal"
            type="text"
            inputMode="decimal"
            pattern="^\d*([.,]\d{0,2})?$"
            inputSize="XS"
            disabled={disabled}
          />
          <AddButton>
            <MainButton
              variant="primary"
              text={t('workOrders:addBudgetItem')}
              onClick={handleAdd}
              disabled={
                disabled ||
                !selectedBudgetItemId ||
                !quantity ||
                budgetItemsLoading
              }
            />
          </AddButton>
        </AmountGroup>
      </SelectorRow>

      {error && (
        <ErrorText>
          <Typography variant="regular" size="S" colorText="red">
            {error}
          </Typography>
        </ErrorText>
      )}
    </SelectorContainer>
  );
};
